// Seção "Técnico" dentro da janela de contato do Whaticket.
// Preencher o QRA transforma o contato em técnico; apagar o QRA desfaz o vínculo.
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Chip, Grid, MenuItem, TextField, Typography } from "@material-ui/core";
import { toast } from "react-toastify";

import suporteApi, { mensagemErro } from "./api";
import { dataBR } from "./comum";

const VAZIO = { qra: "", status: "ATIVO", nome: "", empresa: "", cpf: "", cnpj: "", observacao: "" };

const semPrefixo = (nome) => String(nome || "").replace(/^QRA \d+ · /, "");

const TecnicoContato = forwardRef(({ numero, nomeContato }, ref) => {
  // qualquer usuário edita; a alteração fica registrada (atualizado_por / atualizado_em)
  const admin = true;
  const [f, setF] = useState(VAZIO);
  const [original, setOriginal] = useState(null);
  const [telefones, setTelefones] = useState([]);
  const dig = String(numero || "").replace(/\D/g, "");
  const chave = (v) => {  // DDD + 8 últimos dígitos (com/sem 9 e 55)
    let d = String(v || "").replace(/\D/g, "");
    if (d.length >= 12 && d.startsWith("55")) d = d.slice(2);
    return d.length >= 10 ? d.slice(0, 2) + d.slice(-8) : d;
  };
  const removerTel = async (campo) => {
    try {
      const { data } = await suporteApi.delete(`/api/whaticket/contatos/${dig}/tecnico/telefones/${encodeURIComponent(campo)}`);
      setTelefones(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  useEffect(() => {
    setF(VAZIO);
    setOriginal(null);
    setTelefones([]);
    const d = String(numero || "").replace(/\D/g, "");
    if (d.length < 10) return;
    suporteApi
      .get(`/api/whaticket/contatos/${d}/tecnico`)
      .then(({ data }) => {
        if (data) {
          setOriginal(data);
          setTelefones(data.telefones || []);
          setF({
            qra: String(data.qra), status: data.status, nome: data.nome, empresa: data.empresa || "",
            cpf: data.cpf || "", cnpj: data.cnpj || "", observacao: data.observacao || "",
          });
        }
      })
      .catch(() => {});
  }, [numero]);

  // chamado pela janela de contato depois de salvar o contato no Whaticket
  useImperativeHandle(ref, () => ({
    salvar: async (numeroSalvo, extras = []) => {
      if (!admin) return;
      const d = String(numeroSalvo || numero || "").replace(/\D/g, "");
      if (f.qra === "") {
        if (original) await suporteApi.delete(`/api/whaticket/contatos/${d}/tecnico`);
        return;
      }
      await suporteApi.put(`/api/whaticket/contatos/${d}/tecnico`, {
        ...f, qra: Number(f.qra), nome: f.nome || semPrefixo(nomeContato),
      });
      const ja = new Set(telefones.map((t) => chave(t.telefone)));
      for (const tel of extras) {
        if (ja.has(chave(tel))) continue;
        try {
          await suporteApi.post(`/api/whaticket/contatos/${d}/tecnico/telefones`, { telefone: tel });
        } catch (err) {
          toast.warn(`${tel}: ${mensagemErro(err)}`);
        }
      }
    },
  }));

  const campo = (nome, extra = {}) => ({
    value: f[nome] ?? "", onChange: (e) => setF({ ...f, [nome]: e.target.value }),
    fullWidth: true, size: "small", variant: "outlined", margin: "dense", disabled: !admin, ...extra,
  });

  return (
    <>
      <Typography variant="subtitle1" style={{ marginTop: 12 }}>Técnico</Typography>
      <Typography variant="caption" color="textSecondary">
        {original
          ? `Técnico cadastrado · alterado por ${original.atualizado_por || "-"} em ${dataBR(original.atualizado_em)}`
          : "Preencha o QRA se este contato for um técnico."}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={4}><TextField label="QRA" type="number" {...campo("qra")} /></Grid>
        <Grid item xs={8}>
          <TextField select label="Situação" {...campo("status")}>
            {["ATIVO", "BLOQUEADO", "DESLIGADO"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField label="Nome do técnico" placeholder={semPrefixo(nomeContato)} {...campo("nome")} />
        </Grid>
        <Grid item xs={4}><TextField label="Empresa" {...campo("empresa")} /></Grid>
        <Grid item xs={4}><TextField label="CPF" {...campo("cpf")} /></Grid>
        <Grid item xs={4}><TextField label="CNPJ" {...campo("cnpj")} /></Grid>
        <Grid item xs={12}><TextField label="Observação" multiline rows={2} {...campo("observacao")} /></Grid>
      </Grid>
      {original && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 10 }}>Telefones do técnico</Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            Ligados ao QRA {original.qra}: as mensagens de qualquer um caem no mesmo técnico. Para incluir, use
            “+ Adicionar número” em Dados do contato e salve.
          </Typography>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" }}>
            {telefones.map((t) => {
              const este = chave(t.telefone) === chave(dig);
              return (
                <Chip key={t.campo} size="small" color={este ? "primary" : "default"}
                  label={`${t.telefone}${este ? " (este contato)" : ""}`}
                  onDelete={este ? undefined : () => removerTel(t.campo)} />
              );
            })}
          </div>
        </>
      )}
    </>
  );
});

export default TecnicoContato;
